"use client"

import type React from "react"

import { useState, useRef, type FormEvent } from "react"
import { Button, Form, Input } from "reactstrap"
import RenderInput from "../../../commonComponents/RenderInput"

interface LoanResult {
  monthlyPayment: number
  totalInterest: number
  totalCost: number
}

const LoanCalcForm = () => {
  // Refs for form inputs
  const totalPriceRef = useRef<HTMLInputElement>(null)
  const downPaymentRef = useRef<HTMLInputElement>(null)
  const interestRateRef = useRef<HTMLInputElement>(null)
  const loanTermRef = useRef<HTMLInputElement>(null)

  // State for showing results
  const [showResults, setShowResults] = useState<boolean>(false)
  const [results, setResults] = useState<LoanResult | null>(null)

  // Calculate loan
  const calculateLoan = (e: FormEvent) => {
  e.preventDefault();

  // Get values from refs
  const totalPrice = totalPriceRef.current?.value || "0";
  const downPayment = downPaymentRef.current?.value || "0";
  const interestRate = interestRateRef.current?.value || "0";
  const loanTerm = loanTermRef.current?.value || "60";

  // Parse input values
  const price = Number.parseFloat(totalPrice) || 0;
  const down = Number.parseFloat(downPayment) || 0;
  const rate = Number.parseFloat(interestRate) || 0;
  const term = Number.parseInt(loanTerm) || 60;

  // Calculate principal (price - down payment)
  const principal = price - down;

  if (principal <= 0 || rate <= 0 || term <= 0) {
    setResults({
      monthlyPayment: 0,
      totalInterest: 0,
      totalCost: price,
    });
    setShowResults(true);
    return;
  }

  // Calculate simple interest
  const years = term / 12;
  const interestPerAnnum = principal * (rate / 100);
  const totalInterest = interestPerAnnum * years;
  const totalCost = principal + totalInterest;
  const monthlyPayment = totalCost / term;

  setResults({
    monthlyPayment,
    totalInterest,
    totalCost,
  });

  setShowResults(true);
};

  // Format currency to Malaysian Ringgit (MYR)
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("ms-MY", {
      style: "currency",
      currency: "MYR",
      minimumFractionDigits: 2,
    }).format(value)
  }

  // Custom ref forwarding for RenderInput
  const getRefForInput = (ref: React.MutableRefObject<HTMLInputElement | null>) => {
    return {
      ref: (element: HTMLDivElement | null) => {
        if (element) {
          ref.current = element.querySelector("input")
        }
      },
    }
  }

  return (
    <div className="car-detail-sidebar">
      <div className="sidebar-box form-box">
        <div className="login-title">
          <h3>Auto Loan Calculator</h3>
          <h5>Use our calculator to estimate your monthly car payments.</h5>
        </div>
        <Form className="login-form" onSubmit={calculateLoan}>
          <p>Total Price (RM)</p>
          <div {...getRefForInput(totalPriceRef)}>
            <RenderInput placeholder="Enter Total Price" mainClass="form-input" review inputType="number" />
          </div>

          <p>Down Payment (RM)</p>
          <div {...getRefForInput(downPaymentRef)}>
            <RenderInput placeholder="Enter Down Payment" mainClass="form-input" review inputType="number" />
          </div>

          <p>Interest Rate (%)</p>
          <div {...getRefForInput(interestRateRef)}>
            <RenderInput placeholder="Enter Interest Rate" mainClass="form-input" review inputType="number" step="any" />
          </div>

          <p>Loan Term</p>
          <div className="form-input">
            <Input type="select" className="form-control" innerRef={loanTermRef as React.RefObject<HTMLInputElement>}>
              <option value="24">24 months (2 years)</option>
              <option value="36">36 months (3 years)</option>
              <option value="48">48 months (4 years)</option>
              <option value="60">60 months (5 years)</option>
              <option value="72">72 months (6 years)</option>
              <option value="84">84 months (7 years)</option>
              <option value="96">96 months (8 years)</option>
              <option value="108">108 months (9 years)</option>
            </Input>
          </div>

          <Button type="submit" className="btn-solid">
            Calculate Loan
          </Button>
        </Form>

        {/* Results section - only shown after clicking Calculate */}
        {showResults && results && (
          <div className="loan-results mt-4">
            <h4>Loan Summary</h4>
            <div className="result-item d-flex justify-content-between border-bottom py-2">
              <span className="result-label">Monthly Payment:</span>
              <span className="result-value fw-bold">{formatCurrency(results.monthlyPayment)}</span>
            </div>
            <div className="result-item d-flex justify-content-between border-bottom py-2">
              <span className="result-label">Total Interest:</span>
              <span className="result-value fw-bold">{formatCurrency(results.totalInterest)}</span>
            </div>
            <div className="result-item d-flex justify-content-between py-2">
              <span className="result-label">Total Cost:</span>
              <span className="result-value fw-bold">{formatCurrency(results.totalCost)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LoanCalcForm
